import React from "react";
import Box from "@mui/material/Box";
import { BG_IMAGE_CATALOG } from "../lib/bgAssets";
import {
    IPHONE_BG_PLAY_FALLBACK,
    IPHONE_BG_PLAY_WASH,
    isScreenshotIphoneBackgroundKey,
    screenshotIphoneBgSrc,
} from "../lib/screenshotIphoneBackgrounds";

/** Native WW_LIGHT / WW_DARK play gradients (no scene photo). */
const THEME_GRADIENT = {
    light: "linear-gradient(180deg, #38bdf8 0%, #0284c7 28%, #0c4a6e 62%, #082f49 82%, #031525 100%)",
    dark: "linear-gradient(180deg, #22d3ee 0%, #0284c7 28%, #0c4a6e 58%, #082f49 78%, #020617 100%)",
};

const THEME_SURFACE_LIGHT = {
    light: "linear-gradient(180deg, rgba(224, 242, 254, 0.28) 0%, rgba(56, 189, 248, 0.08) 35%, transparent 100%)",
    dark: "linear-gradient(180deg, rgba(34, 211, 238, 0.16) 0%, rgba(2, 6, 23, 0.12) 40%, transparent 100%)",
};

/** Scene photos from word-wheel-quest `assets/bg_image` → `public/bg_images/word-wheel`. */
const BG_BY_KEY = {
    deep_sea: {
        url: BG_IMAGE_CATALOG.deep_sea,
        fallback: "#031525",
        wash: "linear-gradient(180deg, rgba(255, 255, 255, 0.18) 0%, rgba(15, 23, 42, 0.22) 55%, rgba(15, 23, 42, 0.42) 100%)",
        bubbles: true,
    },
    beach: {
        url: BG_IMAGE_CATALOG.beach,
        fallback: "#0c4a6e",
        wash: "linear-gradient(180deg, rgba(255, 255, 255, 0.16) 0%, rgba(14, 116, 144, 0.22) 45%, rgba(3, 21, 37, 0.45) 100%)",
        bubbles: true,
    },
    mountain: {
        url: BG_IMAGE_CATALOG.mountain,
        fallback: "#1e293b",
        wash: "linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(30, 41, 59, 0.28) 50%, rgba(15, 23, 42, 0.48) 100%)",
        bubbles: false,
    },
    road: {
        url: BG_IMAGE_CATALOG.road,
        fallback: "#292524",
        wash: "linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(68, 64, 60, 0.3) 45%, rgba(28, 25, 23, 0.5) 100%)",
        bubbles: false,
    },
    classroom: {
        url: BG_IMAGE_CATALOG.classroom,
        fallback: "#1c1917",
        wash: "linear-gradient(180deg, rgba(255, 255, 255, 0.1) 0%, rgba(68, 64, 60, 0.28) 50%, rgba(28, 25, 23, 0.52) 100%)",
        bubbles: false,
    },
    flowers: {
        url: BG_IMAGE_CATALOG.flowers,
        fallback: "#365314",
        wash: "linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(63, 98, 18, 0.25) 50%, rgba(26, 46, 5, 0.48) 100%)",
        bubbles: false,
    },
    island: {
        url: BG_IMAGE_CATALOG.island,
        fallback: "#0e7490",
        wash: "linear-gradient(180deg, rgba(255, 255, 255, 0.16) 0%, rgba(14, 116, 144, 0.24) 45%, rgba(8, 47, 73, 0.48) 100%)",
        bubbles: true,
    },
    tropical_island: {
        url: BG_IMAGE_CATALOG.tropical_island,
        fallback: "#155e75",
        wash: "linear-gradient(180deg, rgba(255, 255, 255, 0.15) 0%, rgba(8, 145, 178, 0.22) 45%, rgba(8, 47, 73, 0.5) 100%)",
        bubbles: true,
    },
    urban: {
        url: BG_IMAGE_CATALOG.urban,
        fallback: "#1e293b",
        wash: "linear-gradient(180deg, rgba(255, 255, 255, 0.1) 0%, rgba(51, 65, 85, 0.3) 50%, rgba(15, 23, 42, 0.55) 100%)",
        bubbles: false,
    },
    village: {
        url: BG_IMAGE_CATALOG.village,
        fallback: "#44403c",
        wash: "linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(87, 83, 78, 0.28) 50%, rgba(28, 25, 23, 0.52) 100%)",
        bubbles: false,
    },
};

const BUBBLES = [
    { left: "8%", size: 28, delay: "0s", duration: "11s", bottom: "4%" },
    { left: "22%", size: 18, delay: "1.2s", duration: "13s", bottom: "0%" },
    { left: "38%", size: 42, delay: "0.4s", duration: "15s", bottom: "8%" },
    { left: "55%", size: 22, delay: "2s", duration: "12s", bottom: "2%" },
    { left: "70%", size: 34, delay: "0.8s", duration: "14s", bottom: "6%" },
    { left: "84%", size: 16, delay: "1.6s", duration: "10s", bottom: "1%" },
    { left: "14%", size: 24, delay: "2.4s", duration: "16s", bottom: "10%" },
    { left: "48%", size: 20, delay: "3s", duration: "11s", bottom: "3%" },
    { left: "62%", size: 36, delay: "1s", duration: "13s", bottom: "7%" },
    { left: "90%", size: 14, delay: "2.2s", duration: "12s", bottom: "5%" },
];

function BubblesLayer({ dimmer = false }) {
    return (
        <Box
            sx={{
                "@keyframes wwBubbleRise": {
                    "0%": { transform: "translateY(0) scale(0.85)", opacity: 0 },
                    "8%": { opacity: dimmer ? 0.18 : 0.28 },
                    "75%": { opacity: dimmer ? 0.12 : 0.2 },
                    "100%": { transform: "translateY(-120%) scale(0.95)", opacity: 0 },
                },
            }}
        >
            {BUBBLES.map((b, i) => (
                <Box
                    key={i}
                    sx={{
                        position: "absolute",
                        left: b.left,
                        bottom: b.bottom,
                        width: b.size,
                        height: b.size,
                        borderRadius: "50%",
                        bgcolor: "rgba(186, 230, 253, 0.28)",
                        border: "1px solid rgba(224, 242, 254, 0.35)",
                        animation: `wwBubbleRise ${b.duration} linear ${b.delay} infinite`,
                    }}
                />
            ))}
        </Box>
    );
}

/**
 * Light/dark → ocean gradient only (native WW_LIGHT / WW_DARK).
 * Random → scene photo (native WW_RANDOM over bg_image).
 */
function resolvePhotoConfig(background) {
    if (typeof background === "string" && /^https?:\/\//i.test(background)) {
        return {
            url: background,
            fallback: IPHONE_BG_PLAY_FALLBACK,
            wash: IPHONE_BG_PLAY_WASH,
            bubbles: false,
        };
    }
    if (BG_BY_KEY[background]) {
        return BG_BY_KEY[background];
    }
    if (isScreenshotIphoneBackgroundKey(background)) {
        const url = screenshotIphoneBgSrc(background);
        if (!url) return null;
        return {
            url,
            fallback: IPHONE_BG_PLAY_FALLBACK,
            wash: IPHONE_BG_PLAY_WASH,
            bubbles: false,
        };
    }
    return BG_BY_KEY.deep_sea;
}

export default function PlayBackground({
    background = "deep_sea",
    theme = "random",
    showBubbles = true,
    showWash = true,
}) {
    const cfg = resolvePhotoConfig(background);
    const usePhoto = theme === "random" && Boolean(cfg?.url);

    return (
        <Box
            aria-hidden
            sx={{
                position: "absolute",
                inset: 0,
                overflow: "hidden",
                pointerEvents: "none",
                zIndex: 0,
            }}
        >
            {usePhoto ? (
                <>
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            backgroundColor: cfg.fallback,
                            backgroundImage: `url("${cfg.url}")`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            backgroundRepeat: "no-repeat",
                        }}
                    />
                    {showWash ? (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                background: cfg.wash,
                            }}
                        />
                    ) : null}
                    {cfg.bubbles && showBubbles ? <BubblesLayer /> : null}
                </>
            ) : (
                <>
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            background: THEME_GRADIENT[theme] || THEME_GRADIENT.light,
                        }}
                    />
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: "42%",
                            background: THEME_SURFACE_LIGHT[theme] || THEME_SURFACE_LIGHT.light,
                        }}
                    />
                    {showBubbles ? <BubblesLayer dimmer={theme === "dark"} /> : null}
                </>
            )}
        </Box>
    );
}
